interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

/** Simple in-memory TTL cache with LRU eviction */
export class Cache {
  private store = new Map<string, CacheEntry<unknown>>();
  private readonly maxEntries: number;

  constructor(maxEntries = 1024) {
    this.maxEntries = maxEntries;
  }

  get<T>(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) {
      return undefined;
    }
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return undefined;
    }
    // Move to end (most recently used) — Map iteration order is insertion order
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value as T;
  }

  set<T>(key: string, value: T, ttlMs: number): void {
    // If key exists, delete first so it moves to end
    if (this.store.has(key)) {
      this.store.delete(key);
    }
    // Evict entries if at capacity — prefer removing expired entries first
    if (this.store.size >= this.maxEntries) {
      const now = Date.now();
      for (const [k, e] of this.store) {
        if (now > e.expiresAt) {
          this.store.delete(k);
        }
        if (this.store.size < this.maxEntries) {
          break;
        }
      }
      // Fall back to LRU eviction if still at capacity
      while (this.store.size >= this.maxEntries) {
        const oldest = this.store.keys().next().value;
        if (oldest !== undefined) {
          this.store.delete(oldest);
        } else {
          break;
        }
      }
    }
    this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }

  has(key: string): boolean {
    const entry = this.store.get(key);
    if (!entry) {
      return false;
    }
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return false;
    }
    return true;
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }

  get size(): number {
    return this.store.size;
  }
}
